'use client';

import { cn } from '@/utils/style';
import { Whatsapp } from './icons/whatsapp';
import { Tiktok } from './icons/tiktok';
import { Instagram } from './icons/instagram';
import { Facebook } from './icons/facebook';
import { Brand } from './icons/brand';
import { OpenRent } from './icons/openrent';
import { OnTheMarket } from './icons/onthemarket';
import { Rightmove } from './icons/rightmove';
import { Zoopla } from './icons/zoopla';
import { SectionShadow, SectionSmallShadow } from './icons/sectionbg';
import { Logo } from './icons/logo';
import { Container, Title } from '../section';
import { Letter } from './icons/Letter';
import { Message } from './icons/Message';
import { Phone } from './icons/phone';
import { VideoIcon } from './icons/Video';

const styleBorder = true;

export default function NetworkVisualization() {
  return (
    <div className="relative overflow-hidden">
      <Title
        text="Centralised Lead & Calendar Console"
        className="mx-auto -mb-12 w-full max-w-screen-lg font-medium max-sm:mb-0"
      />
      <SectionShadow
        className="absolute inset-0 w-full opacity-30"
        width="100%"
      />
      {styleBorder ? (
        false
      ) : (
        <img
          src="/assets/background/bg_brand.png"
          className="absolute left-1/2 top-[10px] w-[1122px] -translate-x-1/2"
          alt=""
          srcSet=""
        />
      )}
      <Container className="relative flex h-[1000px] max-w-[1920px] justify-center max-xl:h-[1000px] max-md:h-[700px] max-sm:h-[360px]">
        {/* Center logo */}
        <div
          className={cn(
            'absolute left-[51%] top-[60%] md:w-[800px] aspect-square -translate-x-1/2 -translate-y-1/2 rounded-full max-md:hidden',
            styleBorder && 'border border-[#5B67E6]/60'
          )}
        >
          <SectionSmallShadow />
        </div>
        <CircleBrand>
          <CircleSocial>
            <CircleLogo />
          </CircleSocial>
        </CircleBrand>
      </Container>
    </div>
  );
}

function GradientButton({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex gap-1 items-center justify-center rounded-full text-white h-16 max-md:h-9 lg:px-2',
        className
      )}
      style={{
        background:
          'linear-gradient(90deg, #150343 0%, #4906D0 51%, #7907EE 100%)'
      }}
    >
      {children}
    </div>
  );
}

function CircleButton({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'bg-[#1C2468]/40 aspect-square rotate-12 flex items-center justify-center rounded-full overflow-hidden border border-[#7B83D8]/40 lg:w-20 shadow max-md:w-9',
        className
      )}
    >
      {children}
    </div>
  );
}

function CircleBrand({ children }: { children: React.ReactNode }) {
  return (
    <div
      className={cn(
        'absolute left-1/2 top-[52%] xl:top-[55%] -translate-x-1/2 -translate-y-1/2 md:w-[781px] max-md:w-[65%] aspect-square rounded-full flex justify-center items-center',
        styleBorder && 'border border-[#5B67E6]/60'
      )}
    >
      {/* Social Media Icons */}
      {children}

      {/* Rightmove */}
      <div className="absolute left-1/2 top-0 z-10 -translate-x-1/2 -translate-y-1/2">
        <GradientButton className="w-[185px] max-md:w-24">
          <Rightmove className="max-md:px-4 w-full h-full" />
        </GradientButton>
      </div>

      {/* Zoopla */}
      <div className="absolute right-0 top-1/3 z-10 -translate-y-1/2 translate-x-1/2">
        <GradientButton className="w-[178px] max-md:w-24">
          <Zoopla className="max-md:px-4 w-full h-full" />
        </GradientButton>
      </div>

      {/* OnTheMarket */}
      <div className="absolute left-0 top-1/3 z-10 -translate-x-1/2 -translate-y-1/2">
        <GradientButton className="w-[178px] max-md:w-24">
          <OnTheMarket className="max-md:px-4 w-full h-full" />
        </GradientButton>
      </div>

      <div className="absolute left-0 top-[70%] z-10 -translate-x-1/2 -translate-y-1/2">
        <GradientButton className="w-[276px] max-md:w-44">
          <Brand className="max-md:w-5" />
          <span className="text-xl text-white max-md:text-xs">
            {`Nestflo 'Flo'`}
          </span>
        </GradientButton>
      </div>

      {/* Branded Chatbot */}
      {/* <div className="absolute bottom-0 left-1/2 z-10 -translate-x-1/2 translate-y-1/2">
        <GradientButton className="w-[276px] max-md:w-44">
          <Brand className="max-md:w-5" />
          <span className="text-xl text-white max-md:text-xs">
            {`Nestflo 'Flo'`}
          </span>
        </GradientButton>
      </div> */}

      {/* OpenRent */}
      <div className="absolute right-0 top-[70%] z-10 -translate-y-1/2 translate-x-1/2">
        <GradientButton className="w-[156px] max-md:w-24">
          <OpenRent className="max-md:px-4 w-full h-full" />
        </GradientButton>
      </div>
    </div>
  );
}
function CircleSocial({ children }: { children: React.ReactNode }) {
  return (
    <div
      className={cn(
        'flex items-center justify-center w-[58.97%] aspect-square -rotate-12 rounded-full ',
        styleBorder && 'border border-[#5B67E6]/60'
      )}
    >
      {children}
      {/* Facebook */}
      <div className="absolute right-0 top-1/2 z-10 -translate-y-1/2 translate-x-1/2">
        <CircleButton className="max-xs:w-8 lg:w-20">
          <VideoIcon className="w-full h-full p-2 lg:p-4" />
        </CircleButton>
      </div>

      {/* Instagram */}
      <div className="absolute left-0 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2">
        <CircleButton className="max-xs:w-9 lg:w-24">
          <Instagram className="w-full h-full p-2 lg:p-4" />
        </CircleButton>
      </div>

      {/* TikTok */}
      <div className="absolute bottom-0 left-1/2 z-10 -translate-x-1/2 translate-y-1/2">
        <CircleButton className="max-xs:w-8 lg:w-20">
          <Tiktok className="w-full h-full p-2 lg:p-4" />
        </CircleButton>
      </div>

      {/* WhatsApp */}
      <div className="absolute left-1/2 top-0 z-10 -translate-x-1/2 -translate-y-1/2">
        <CircleButton className="max-xs:w-9 lg:w-24">
          <Facebook className="w-full h-full p-2 lg:p-4" />
        </CircleButton>
      </div>
    </div>
  );
}

function CircleLogo() {
  return (
    <div
      className={cn(
        'w-[69.56%] aspect-square rounded-full rotate-45 flex items-center justify-center max-xs:w-[50%]',
        styleBorder && 'border border-[#5B67E6]/60'
      )}
      style={{
        backgroundImage:
          'repeating-radial-gradient(circle at center, rgba(19,23,50,10) 0, rgba(19,23,70,20) 18%)'
      }}
    >
      <div className="flex aspect-square w-[37.5%] rotate-[-33deg] items-center justify-center overflow-hidden rounded-full bg-gradient-to-b from-[#241C8D] to-[#444EAC]">
        <Logo className="w-4/5 max-md:hidden" />
        <Brand className="hidden max-md:block max-md:w-5" />
      </div>
      <div className="absolute right-0 top-1/2 z-10 -translate-y-1/2 translate-x-1/2">
        <CircleButton className="rotate-[-33deg] max-md:w-6 lg:w-[60px]">
          <Phone className="w-full h-full p-1 lg:p-4" />
        </CircleButton>
      </div>

      {/* Instagram */}
      <div className="absolute left-0 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2">
        <CircleButton className="rotate-[-33deg] max-md:w-6 lg:w-[60px]">
          <Message className="w-full h-full p-2 max-md:p-1 lg:p-4" />
        </CircleButton>
      </div>

      {/* TikTok */}
      <div className="absolute bottom-0 left-1/2 z-10 -translate-x-1/2 translate-y-1/2">
        <CircleButton className="rotate-[-33deg] max-md:w-6 lg:w-[60px]">
          <Letter className="w-full h-full p-1 lg:p-4" />
        </CircleButton>
      </div>

      {/* WhatsApp */}
      <div className="absolute left-1/2 top-0 z-10 -translate-x-1/2 -translate-y-1/2">
        <CircleButton className="rotate-[-33deg] max-md:w-6 lg:w-[60px]">
          <Whatsapp className="w-full h-full p-1 lg:p-4" />
        </CircleButton>
      </div>
    </div>
  );
}
